import ErrorMessage from '@/components/errorMessage';
import MultiSelectField from '@/components/muiltipleSelectField';
import NumberField from '@/components/numberField';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { useUserContext } from '@/contexts/userContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Divider, FormLabel, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TenantSettings = () => {
  const apiContext = useApiContext();
  const { t } = useTranslation();
  const { toast } = useNotificationContext();

  const { user, hasPermission } = useUserContext();
  const [containerRef, containerSize] = useElementSize();

  const [tenantSettings, setTenantSettings] = useState<any>({});

  const [llmOptions, setLlmOptions] = useState<any[]>([]);
  const [llmModels, setLlmModels] = useState<any[]>([]);

  const [embeddingLlmOptions, setEmbeddingLlmOptions] = useState<any[]>([]);
  const [embeddingLlmModels, setEmbeddingLlmModels] = useState<any[]>([]);

  const [availableEventDefs, setAvailableEventDefs] = useState<{ eventName: string; eventLabel: string }[]>([]);

  const [changed, setChanged] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getSettings = async () => {
      try {
        setLoading(true);
        const [settigs, evDefs] = await Promise.all([
          apiContext.getTenantSettings(),
          apiContext.getAllEventDefs().catch(() => []),
        ]);
        setTenantSettings(settigs);
        setAvailableEventDefs(evDefs || []);
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getConversationLlms = async () => {
      try {
        setLoading(true);
        const llms = await apiContext.getConversationLlms();
        setLlmOptions(
          llms.map((llm) => {
            return { value: llm, label: llm };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getEmbeddingsLlms = async () => {
      try {
        const llms = await apiContext.getEmbeddingsLlms();
        setEmbeddingLlmOptions(
          llms.map((llm: string) => {
            return { value: llm, label: llm };
          }),
        );
      } catch {
        setError(error);
      }
    };

    getSettings();
    getConversationLlms();
    getEmbeddingsLlms();
  }, []);

  useEffect(() => {
    const getConversationLlmModels = async () => {
      try {
        setLoading(true);
        const llmModels = await apiContext.getConversationLlmModels(
          tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME,
        );
        setLlmModels(
          llmModels.map((model) => {
            return { value: model.modelCode, label: model.modelName };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    if (tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME) {
      getConversationLlmModels();
    } else {
      setLlmModels([]);
    }
  }, [tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME]);

  useEffect(() => {
    const getEmbeddingsModels = async () => {
      try {
        setLoading(true);
        const models = await apiContext.getEmbeddingsModels(tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME);
        setEmbeddingLlmModels(
          (models || []).map((model: any) => {
            return { value: model.modelCode, label: model.modelName || model.modelCode };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    if (tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME) {
      getEmbeddingsModels();
    } else {
      setEmbeddingLlmModels([]);
    }
  }, [tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME]);

  const onChange = (settingName: string, value: any) => {
    setTenantSettings({
      ...tenantSettings,
      USER_SURVEY_PROCESSING_LLM_SETTINGS: {
        ...tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS,
        [settingName]: value,
      },
    });
    setChanged(true);
  };

  const onEmbeddingChange = (settingName: string, value: any) => {
    setTenantSettings({
      ...tenantSettings,
      LLM_EMBEDDING_SETTINGS: {
        ...tenantSettings.LLM_EMBEDDING_SETTINGS,
        [settingName]: value,
      },
    });
    setChanged(true);
  };

  const onActivityFeedChange = (settingName: string, value: any) => {
    setTenantSettings({
      ...tenantSettings,
      ACTIVITY_FEED_SETTINGS: {
        ...tenantSettings.ACTIVITY_FEED_SETTINGS,
        [settingName]: value,
      },
    });
    setChanged(true);
  };

  const submitChanges = async () => {
    try {
      const errors = {} as any;
      let hasError = false;
      if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_NAME) {
        errors.LLM_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_NAME === 'openai') {
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_OPENAI_MODEL) {
          errors.LLM_OPENAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_OPENAI_API_KEY) {
          errors.LLM_OPENAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_NAME === 'azureopenai') {
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_AZURE_OPENAI_MODEL) {
          errors.LLM_AZURE_OPENAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_AZURE_OPENAI_DEPLOYMENT_NAME) {
          errors.LLM_AZURE_OPENAI_DEPLOYMENT_NAME = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_AZURE_OPENAI_API_ENDPOINT) {
          errors.LLM_AZURE_OPENAI_API_ENDPOINT = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_AZURE_OPENAI_API_KEY) {
          errors.LLM_AZURE_OPENAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_NAME === 'geminiai') {
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_GEMINIAI_MODEL) {
          errors.LLM_GEMINIAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_GEMINIAI_API_KEY) {
          errors.LLM_GEMINIAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_NAME === 'claudeai') {
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_CLAUDE_MODEL) {
          errors.LLM_CLAUDE_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_CLAUDE_API_KEY) {
          errors.LLM_CLAUDE_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (!tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_MAXTOKENS) {
        errors.LLM_MAXTOKENS = t('common.errors.requiredField');
        hasError = true;
      }
      if (
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_TEMPERATURE < 0 ||
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_TEMPERATURE > 2
      ) {
        errors.LLM_TEMPERATURE = t('settings.conversationConfig.errors.temperature');
        hasError = true;
      }
      if (
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_FREQUENCY_PENALTY < -2 ||
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_FREQUENCY_PENALTY > 2
      ) {
        errors.LLM_FREQUENCY_PENALTY = t('settings.conversationConfig.errors.frequencyP');
        hasError = true;
      }
      if (
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_PRESENCE_PENALTY < -2 ||
        tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS.LLM_PRESENCE_PENALTY > 2
      ) {
        errors.LLM_PRESENCE_PENALTY = t('settings.conversationConfig.errors.presenceP');
        hasError = true;
      }

      if (tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME) {
        const emb = tenantSettings.LLM_EMBEDDING_SETTINGS;
        if (emb.LLM_NAME === 'openai') {
          if (!emb.LLM_OPENAI_EMBEDDINGS_MODEL && !emb.LLM_OPENAI_MODEL) {
            errors.EMBEDDING_LLM_OPENAI_MODEL = t('common.errors.requiredField');
            hasError = true;
          }
          if (!emb.LLM_OPENAI_API_KEY) {
            errors.EMBEDDING_LLM_OPENAI_API_KEY = t('common.errors.requiredField');
            hasError = true;
          }
        }
        if (emb.LLM_NAME === 'azureopenai') {
          if (
            !emb.LLM_AZURE_OPENAI_EMBEDDINGS_MODEL &&
            !emb.EMBEDDINGS_AZURE_OPENAI_MODEL &&
            !emb.LLM_AZURE_OPENAI_MODEL
          ) {
            errors.EMBEDDING_LLM_AZURE_OPENAI_MODEL = t('common.errors.requiredField');
            hasError = true;
          }
          if (!emb.LLM_AZURE_OPENAI_DEPLOYMENT_NAME && !emb.EMBEDDINGS_AZURE_OPENAI_DEPLOYMENT_NAME) {
            errors.EMBEDDING_LLM_AZURE_OPENAI_DEPLOYMENT_NAME = t('common.errors.requiredField');
            hasError = true;
          }
          if (!emb.LLM_AZURE_OPENAI_API_ENDPOINT) {
            errors.EMBEDDING_LLM_AZURE_OPENAI_API_ENDPOINT = t('common.errors.requiredField');
            hasError = true;
          }
          if (!emb.LLM_AZURE_OPENAI_API_KEY) {
            errors.EMBEDDING_LLM_AZURE_OPENAI_API_KEY = t('common.errors.requiredField');
            hasError = true;
          }
        }
        if (emb.LLM_NAME === 'geminiai') {
          if (!emb.LLM_GEMINIAI_EMBEDDINGS_MODEL && !emb.LLM_GEMINIAI_MODEL) {
            errors.EMBEDDING_LLM_GEMINIAI_MODEL = t('common.errors.requiredField');
            hasError = true;
          }
          if (!emb.LLM_GEMINIAI_API_KEY) {
            errors.EMBEDDING_LLM_GEMINIAI_API_KEY = t('common.errors.requiredField');
            hasError = true;
          }
        }
      }

      setErrors(errors);
      if (hasError) {
        return;
      }
      setLoading(true);
      await apiContext.updateTenantSettings(tenantSettings);
      setChanged(false);
      toast({ message: 'Changes saved' });
    } catch (error: any) {
      setError(error);
    }
    setLoading(false);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: 'background.paper',
      }}
    >
      {/* Section header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Tenant Settings
        </Typography>
      </Stack>

      {/* Scrollable form */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2.5, pt: 1.5 }}>
        <Grid container spacing={1.5} sx={{ width: '100%' }}>
          {error && (
            <Grid size={12}>
              <ErrorMessage error={error} />
            </Grid>
          )}
          <Grid size={12}>
            <Divider sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                User survey processing LLM settings
              </Typography>
            </Divider>
          </Grid>
          <Grid size={6}>
            <FormLabel required>{'Select LLM'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <SelectField
              options={llmOptions}
              value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME}
              required
              error={errors.LLM_NAME}
              onChange={(value) => onChange('LLM_NAME', value)}
            />
          </Grid>
          {tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME === 'openai' && (
            <>
              <Grid size={6} sx={{ borderTop: 1, borderColor: 'divider' }}>
                <FormLabel required>{'OpenAI model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={llmModels}
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_OPENAI_MODEL}
                  required
                  error={errors.LLM_OPENAI_MODEL}
                  onChange={(value) => onChange('LLM_OPENAI_MODEL', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'OpenAI api Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_OPENAI_API_KEY}
                  required
                  error={errors.LLM_OPENAI_API_KEY}
                  onChange={(value) => onChange('LLM_OPENAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'OpenAI project'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_OPENAI_API_PROJECT}
                  required
                  error={errors.LLM_OPENAI_API_PROJECT}
                  onChange={(value) => onChange('LLM_OPENAI_API_PROJECT', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'OpenAI organisation'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_OPENAI_API_ORGANISATION}
                  required
                  error={errors.LLM_OPENAI_API_ORGANISATION}
                  onChange={(value) => onChange('LLM_OPENAI_API_ORGANISATION', value)}
                />
              </Grid>
            </>
          )}
          {tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME === 'azureopenai' && (
            <>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI Deployment'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_AZURE_OPENAI_DEPLOYMENT_NAME}
                  required
                  error={errors.LLM_AZURE_OPENAI_DEPLOYMENT_NAME}
                  onChange={(value) => onChange('LLM_AZURE_OPENAI_DEPLOYMENT_NAME', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={llmModels}
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_AZURE_OPENAI_MODEL}
                  required
                  error={errors.LLM_AZURE_OPENAI_MODEL}
                  onChange={(value) => onChange('LLM_AZURE_OPENAI_MODEL', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI endpoint'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_AZURE_OPENAI_API_ENDPOINT}
                  required
                  error={errors.LLM_AZURE_OPENAI_API_ENDPOINT}
                  onChange={(value) => onChange('LLM_AZURE_OPENAI_API_ENDPOINT', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI api key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_AZURE_OPENAI_API_KEY}
                  required
                  error={errors.LLM_AZURE_OPENAI_API_KEY}
                  onChange={(value) => onChange('LLM_AZURE_OPENAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
            </>
          )}
          {tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME === 'geminiai' && (
            <>
              <Grid size={6}>
                <FormLabel required>{'Gemini api Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_GEMINIAI_API_KEY}
                  required
                  error={errors.LLM_GEMINIAI_API_KEY}
                  onChange={(value) => onChange('LLM_GEMINIAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Gemini model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={llmModels}
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_GEMINIAI_MODEL}
                  required
                  error={errors.LLM_GEMINIAI_MODEL}
                  onChange={(value) => onChange('LLM_GEMINIAI_MODEL', value)}
                />
              </Grid>
            </>
          )}
          {tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_NAME === 'claudeai' && (
            <>
              <Grid size={6}>
                <FormLabel required>{'Claude api Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_CLAUDE_API_KEY}
                  required
                  error={errors.LLM_CLAUDE_API_KEY}
                  onChange={(value) => onChange('LLM_CLAUDE_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Claude model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={llmModels}
                  value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_CLAUDE_MODEL}
                  required
                  error={errors.LLM_CLAUDE_MODEL}
                  onChange={(value) => onChange('LLM_CLAUDE_MODEL', value)}
                />
              </Grid>
            </>
          )}
          <Grid size={6}>
            <FormLabel required>{'Max response tokens'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <NumberField
              value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_MAXTOKENS || 200000}
              required
              min={1}
              step={1}
              error={errors.LLM_MAXTOKENS}
              onChange={(value) => onChange('LLM_MAXTOKENS', value)}
            />
          </Grid>
          <Grid size={6}>
            <FormLabel required>{'Temperature'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <NumberField
              value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_TEMPERATURE}
              required
              min={0}
              step={0.1}
              max={2}
              error={errors.LLM_TEMPERATURE}
              onChange={(value) => onChange('LLM_TEMPERATURE', value)}
            />
          </Grid>
          <Grid size={6}>
            <FormLabel required>{'Frequency Penalty'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <NumberField
              value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_FREQUENCY_PENALTY}
              required
              min={-2}
              max={2}
              error={errors.LLM_FREQUENCY_PENALTY}
              onChange={(value) => onChange('LLM_FREQUENCY_PENALTY', value)}
            />
          </Grid>
          <Grid size={6}>
            <FormLabel required>{'Presence Penalty'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <NumberField
              value={tenantSettings.USER_SURVEY_PROCESSING_LLM_SETTINGS?.LLM_PRESENCE_PENALTY}
              required
              min={-2}
              max={2}
              error={errors.LLM_PRESENCE_PENALTY}
              onChange={(value) => onChange('LLM_PRESENCE_PENALTY', value)}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                LLM Embedding Settings
              </Typography>
            </Divider>
          </Grid>
          <Grid size={6}>
            <FormLabel>{'Select Embedding LLM'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <SelectField
              options={embeddingLlmOptions}
              value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME}
              error={errors.EMBEDDING_LLM_NAME}
              onChange={(value) => onEmbeddingChange('LLM_NAME', value)}
            />
          </Grid>
          {tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME === 'openai' && (
            <>
              <Grid size={6} sx={{ borderTop: 1, borderColor: 'divider' }}>
                <FormLabel required>{'OpenAI Embedding Model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={embeddingLlmModels}
                  value={
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_EMBEDDINGS_MODEL ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_MODEL
                  }
                  required
                  error={errors.EMBEDDING_LLM_OPENAI_MODEL}
                  onChange={(value) => {
                    onEmbeddingChange('LLM_OPENAI_EMBEDDINGS_MODEL', value);
                    onEmbeddingChange('LLM_OPENAI_MODEL', value);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'OpenAI API Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_API_KEY}
                  required
                  error={errors.EMBEDDING_LLM_OPENAI_API_KEY}
                  onChange={(value) => onEmbeddingChange('LLM_OPENAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel>{'Embedding Dimensions'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <NumberField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_EMBEDDINGS_DIMENSIONS || 1536}
                  min={1}
                  step={1}
                  error={''}
                  onChange={(value) => onEmbeddingChange('LLM_OPENAI_EMBEDDINGS_DIMENSIONS', Number(value))}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel>{'OpenAI Project'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_API_PROJECT}
                  onChange={(value) => onEmbeddingChange('LLM_OPENAI_API_PROJECT', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel>{'OpenAI Organisation'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_OPENAI_API_ORGANISATION}
                  onChange={(value) => onEmbeddingChange('LLM_OPENAI_API_ORGANISATION', value)}
                />
              </Grid>
            </>
          )}
          {tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME === 'azureopenai' && (
            <>
              <Grid size={6} sx={{ borderTop: 1, borderColor: 'divider' }}>
                <FormLabel required>{'Azure OpenAI Deployment'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_DEPLOYMENT_NAME ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.EMBEDDINGS_AZURE_OPENAI_DEPLOYMENT_NAME
                  }
                  required
                  error={errors.EMBEDDING_LLM_AZURE_OPENAI_DEPLOYMENT_NAME}
                  onChange={(value) => {
                    onEmbeddingChange('LLM_AZURE_OPENAI_DEPLOYMENT_NAME', value);
                    onEmbeddingChange('EMBEDDINGS_AZURE_OPENAI_DEPLOYMENT_NAME', value);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI Embedding Model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={embeddingLlmModels}
                  value={
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_EMBEDDINGS_MODEL ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.EMBEDDINGS_AZURE_OPENAI_MODEL ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_MODEL
                  }
                  required
                  error={errors.EMBEDDING_LLM_AZURE_OPENAI_MODEL}
                  onChange={(value) => {
                    onEmbeddingChange('LLM_AZURE_OPENAI_EMBEDDINGS_MODEL', value);
                    onEmbeddingChange('EMBEDDINGS_AZURE_OPENAI_MODEL', value);
                    onEmbeddingChange('LLM_AZURE_OPENAI_MODEL', value);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI Endpoint'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_API_ENDPOINT}
                  required
                  error={errors.EMBEDDING_LLM_AZURE_OPENAI_API_ENDPOINT}
                  onChange={(value) => onEmbeddingChange('LLM_AZURE_OPENAI_API_ENDPOINT', value)}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Azure OpenAI API Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_API_KEY}
                  required
                  error={errors.EMBEDDING_LLM_AZURE_OPENAI_API_KEY}
                  onChange={(value) => onEmbeddingChange('LLM_AZURE_OPENAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel>{'Embedding Dimensions'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <NumberField
                  value={
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_AZURE_OPENAI_EMBEDDINGS_DIMENSIONS ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.EMBEDDINGS_AZURE_OPENAI_DIMENSIONS ||
                    1536
                  }
                  min={1}
                  step={1}
                  error={''}
                  onChange={(value) => {
                    onEmbeddingChange('LLM_AZURE_OPENAI_EMBEDDINGS_DIMENSIONS', Number(value));
                    onEmbeddingChange('EMBEDDINGS_AZURE_OPENAI_DIMENSIONS', Number(value));
                  }}
                />
              </Grid>
            </>
          )}
          {tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_NAME === 'geminiai' && (
            <>
              <Grid size={6} sx={{ borderTop: 1, borderColor: 'divider' }}>
                <FormLabel required>{'Gemini Embedding Model'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <SelectField
                  options={embeddingLlmModels}
                  value={
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_GEMINIAI_EMBEDDINGS_MODEL ||
                    tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_GEMINIAI_MODEL
                  }
                  required
                  error={errors.EMBEDDING_LLM_GEMINIAI_MODEL}
                  onChange={(value) => {
                    onEmbeddingChange('LLM_GEMINIAI_EMBEDDINGS_MODEL', value);
                    onEmbeddingChange('LLM_GEMINIAI_MODEL', value);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormLabel required>{'Gemini API Key'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <TextField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_GEMINIAI_API_KEY}
                  required
                  error={errors.EMBEDDING_LLM_GEMINIAI_API_KEY}
                  onChange={(value) => onEmbeddingChange('LLM_GEMINIAI_API_KEY', value)}
                  type="password"
                />
              </Grid>
              <Grid size={6}>
                <FormLabel>{'Embedding Dimensions'}</FormLabel>
              </Grid>
              <Grid size={6}>
                <NumberField
                  value={tenantSettings.LLM_EMBEDDING_SETTINGS?.LLM_GEMINIAI_EMBEDDINGS_DIMENSIONS || 1536}
                  min={1}
                  step={1}
                  error={''}
                  onChange={(value) => onEmbeddingChange('LLM_GEMINIAI_EMBEDDINGS_DIMENSIONS', Number(value))}
                />
              </Grid>
            </>
          )}

          <Grid size={12}>
            <Divider sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Live Activity Feed Settings
              </Typography>
            </Divider>
          </Grid>
          <Grid size={6}>
            <FormLabel>{'Tracked Event Definitions (Comma Separated)'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <MultiSelectField
              options={availableEventDefs.map((def) => def.eventName)}
              value={(tenantSettings.ACTIVITY_FEED_SETTINGS?.ACTIVITY_FEED_EVENT_DEFS || '')
                .split(',')
                .map((s: string) => s.trim())
                .filter((s: string) => s)}
              onChange={(value: string[]) => {
                onActivityFeedChange('ACTIVITY_FEED_EVENT_DEFS', value.join(','));
              }}
            />
          </Grid>
          <Grid size={6}>
            <FormLabel>{'Retention Limit'}</FormLabel>
          </Grid>
          <Grid size={6}>
            <NumberField
              value={tenantSettings.ACTIVITY_FEED_SETTINGS?.ACTIVITY_FEED_RETENTION_LIMIT || 100}
              min={1}
              error={''}
              onChange={(value) => onActivityFeedChange('ACTIVITY_FEED_RETENTION_LIMIT', value)}
            />
          </Grid>
        </Grid>

        {/* Sticky footer inside scroll area — never clipped by TabPanel */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 2,
            pt: 1.5,
            pb: 1.5,
            px: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={submitChanges}
            loading={loading}
            disabled={!changed}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TenantSettings;
